import { readImageBlob, writeImageBlob, deleteImageBlob, clearImageBlobs } from './imageBlobStore.js';
import { signedImageInfo, collectSignedImages, refreshImageSource } from './signedImageUrls.js';

const entries = new Map();
const listeners = new Map();
const blobKeys = new Map();
const renewing = new Map();
let owner = null;
let session = 0;
let timer = null;
let downloading = 0;
const downloadQueue = [];

function active() {
  return typeof document !== 'undefined' && document.visibilityState === 'visible'
    && document.hasFocus() && globalThis.navigator?.onLine !== false;
}

export function storageObjectUrl(value) {
  return blobKeys.get(value) || signedImageInfo(value)?.key || value;
}

function notify(key) {
  for (const callback of listeners.get(key) || []) callback();
}

function release(entry) {
  if (entry.displayUrl?.startsWith('blob:')) {
    const previous = entry.displayUrl;
    blobKeys.delete(previous);
    // React consumers need one turn to replace the previous src before it is revoked.
    setTimeout(() => URL.revokeObjectURL(previous), 0);
  }
  entry.displayUrl = null;
}

function resetEntries() {
  clearTimeout(timer); timer = null;
  session += 1;
  for (const entry of entries.values()) { entry.controller?.abort(); release(entry); }
  entries.clear(); renewing.clear();
  for (const key of listeners.keys()) notify(key);
  drain();
}

export function setStorageOwner(nextOwner) {
  nextOwner = nextOwner || null;
  if (owner === nextOwner) return;
  const previous = owner;
  owner = nextOwner;
  resetEntries();
  if (previous) void clearImageBlobs(previous);
}

export function getStorageSession() { return { owner, session }; }

export function registerStorageResponse(value, captured = getStorageSession()) {
  if (!owner || captured.owner !== owner || captured.session !== session) return;
  for (const image of collectSignedImages(value)) {
    let entry = entries.get(image.key);
    if (!entry) {
      entry = { ...image, urlExpiresAt: image.expiresAt, displayUrl: null, expiresAt: 0, revision: 0, retryAt: 0, failures: 0 };
      entries.set(image.key, entry);
      queueMicrotask(() => notify(image.key));
    } else if (image.signedAt >= entry.signedAt) {
      const changed = image.version && entry.version && image.version !== entry.version;
      entry.url = image.url; entry.urlExpiresAt = image.expiresAt; entry.signedAt = image.signedAt;
      if (image.version) entry.version = image.version;
      // Full generation reads can renew variants that project thumbnails omit.
      if (image.source.kind !== 'images' || entry.source.kind === 'images') entry.source = image.source;
      if (changed) invalidateUrl(image.key);
    }
  }
  schedule();
}

export function onInvalidate(key, callback) {
  const id = storageObjectUrl(key);
  if (!listeners.has(id)) listeners.set(id, new Set());
  listeners.get(id).add(callback);
  schedule();
  return () => {
    listeners.get(id)?.delete(callback);
    if (!listeners.get(id)?.size) {
      listeners.delete(id);
      queueMicrotask(() => {
        if (!listeners.has(id)) {
          const entry = entries.get(id);
          if (entry) {
            entry.revision += 1; entry.controller?.abort(); entry.pending = null;
            release(entry);
          }
        }
      });
    }
    schedule();
  };
}

function schedule() {
  clearTimeout(timer); timer = null;
  if (!owner || !active()) return;
  let due = Infinity;
  for (const key of listeners.keys()) {
    const entry = entries.get(key);
    if (entry && !entry.pending) due = Math.min(due, Math.max(entry.expiresAt, entry.retryAt));
  }
  if (Number.isFinite(due)) timer = setTimeout(refreshActive, Math.max(1, Math.min(due - Date.now(), 2 ** 31 - 1)));
}

function refreshActive() {
  timer = null;
  if (!active()) return;
  for (const key of listeners.keys()) {
    const entry = entries.get(key);
    if (entry && Date.now() >= Math.max(entry.expiresAt, entry.retryAt)) void resolveUrl(key);
  }
  schedule();
}

function activityChanged() {
  if (!active()) {
    clearTimeout(timer); timer = null;
    for (const entry of entries.values()) entry.controller?.abort();
  } else {
    drain(); refreshActive();
  }
}

globalThis.document?.addEventListener('visibilitychange', activityChanged);
for (const event of ['focus', 'blur', 'pageshow', 'online', 'offline']) globalThis.window?.addEventListener(event, activityChanged);
if (import.meta.hot) import.meta.hot.dispose(() => {
  resetEntries();
  globalThis.document?.removeEventListener('visibilitychange', activityChanged);
  for (const event of ['focus', 'blur', 'pageshow', 'online', 'offline']) globalThis.window?.removeEventListener(event, activityChanged);
});

function drain() {
  while (downloadQueue.length && downloading < 6) {
    const task = downloadQueue.shift();
    if (task.session !== session || !active()) { task.resolve(null); continue; }
    downloading += 1;
    task.run().then(task.resolve, task.reject).finally(() => { downloading -= 1; drain(); });
  }
}

function limitedDownload(run) {
  return new Promise((resolve, reject) => {
    downloadQueue.push({ run, resolve, reject, session }); drain();
  });
}

async function renew(entry) {
  const group = JSON.stringify(entry.source);
  let pending = renewing.get(group);
  if (!pending) {
    const epoch = session;
    pending = refreshImageSource(entry.source, () => active() && session === epoch).finally(() => { if (renewing.get(group) === pending) renewing.delete(group); });
    renewing.set(group, pending);
  }
  await pending;
}

export function getCachedUrl(key) {
  if (!key) return null;
  const entry = owner && entries.get(storageObjectUrl(key));
  if (entry) return Date.now() < entry.expiresAt ? entry.displayUrl : null;
  if (/^(blob:|data:)/i.test(key) && !blobKeys.has(key)) return key;
  if (/^https?:/i.test(key) && !signedImageInfo(key)) return key;
  return null;
}

export async function resolveUrl(key) {
  if (!key) return null;
  const cached = getCachedUrl(key);
  if (cached) return cached;
  const entry = owner && entries.get(storageObjectUrl(key));
  if (!entry) return null;
  if (entry.pending) return entry.pending;
  if (Date.now() < entry.retryAt) return entry.displayUrl;
  const captured = getStorageSession();
  const revision = entry.revision;
  const valid = () => captured.session === session && captured.owner === owner && entries.get(entry.key) === entry && entry.revision === revision;
  const pending = (async () => {
    const stored = await readImageBlob(captured.owner, entry.key, entry.version, Date.now());
    if (!valid()) return null;
    if (stored) {
      release(entry); entry.displayUrl = URL.createObjectURL(stored.blob);
      blobKeys.set(entry.displayUrl, entry.key); entry.expiresAt = stored.expiresAt;
      notify(entry.key); return entry.displayUrl;
    }
    if (!active()) return entry.displayUrl;
    return limitedDownload(async () => {
      if (!valid() || !active()) return null;
      if (entry.urlExpiresAt <= Date.now()) await renew(entry);
      if (!valid() || !active()) return null;
      if (entry.urlExpiresAt <= Date.now()) throw new Error('Image is no longer available');
      entry.controller = new AbortController();
      let response;
      try {
        response = await fetch(entry.url, { signal: entry.controller.signal, cache: 'no-store', credentials: 'omit' });
      } catch (error) {
        if (error.name === 'AbortError' || !valid() || !active()) return null;
        // Cross-origin image display can work even when bucket CORS prevents reading its bytes.
        release(entry); entry.displayUrl = entry.url; entry.expiresAt = entry.urlExpiresAt;
        notify(entry.key); return entry.displayUrl;
      }
      if (!response.ok) {
        if ([401, 403].includes(response.status)) entry.urlExpiresAt = 0;
        throw new Error(`Image fetch failed: ${response.status}`);
      }
      const blob = await response.blob();
      if (!valid() || !active()) return null;
      if (!blob.type.startsWith('image/') || !blob.size) throw new Error('Invalid image response');
      const expiresAt = entry.urlExpiresAt;
      await writeImageBlob(captured.owner, entry.key, entry.version, blob, expiresAt);
      if (!valid()) return null;
      release(entry); entry.displayUrl = URL.createObjectURL(blob); blobKeys.set(entry.displayUrl, entry.key);
      entry.expiresAt = expiresAt; entry.failures = 0; entry.retryAt = 0;
      notify(entry.key); return entry.displayUrl;
    });
  })().catch((error) => {
    if (!valid()) return null;
    if (error.name === 'AbortError' || !active()) return entry.displayUrl;
    entry.failures += 1;
    entry.retryAt = Date.now() + Math.min(60_000, 5000 * 2 ** Math.min(entry.failures - 1, 4));
    return entry.displayUrl;
  }).finally(() => {
    if (entry.pending === pending) entry.pending = null;
    schedule();
  });
  entry.pending = pending;
  schedule();
  return pending;
}

export async function resolveUrls(keys) {
  return Object.fromEntries(await Promise.all((keys || []).filter(Boolean).map(async (key) => [key, await resolveUrl(key)])));
}

export function invalidateUrl(key) {
  const id = storageObjectUrl(key);
  const entry = entries.get(id);
  if (!entry) return;
  entry.revision += 1; entry.controller?.abort(); entry.pending = null;
  release(entry); entry.expiresAt = 0; entry.retryAt = 0;
  if (owner) void deleteImageBlob(owner, id);
  notify(id); schedule();
}

export function invalidatePrefix(prefix) {
  for (const key of entries.keys()) if (key.startsWith(prefix) || key.includes(`/${prefix}`)) invalidateUrl(key);
}

export function retryOnError(key) {
  const entry = entries.get(storageObjectUrl(key));
  if (!entry || Date.now() < (entry.errorRetryAfter || 0)) return;
  entry.errorRetryAfter = Date.now() + 60_000; entry.urlExpiresAt = 0;
  invalidateUrl(entry.key);
}

export function clearCache() {
  const previous = owner;
  owner = null; resetEntries();
  if (previous) void clearImageBlobs(previous);
}

export default { resolveUrl, resolveUrls, invalidateUrl, invalidatePrefix, clearCache, getCachedUrl, onInvalidate, retryOnError };
